import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
} from 'react';

interface InteractiveTutorialContextType {
  isActive: boolean;
  shouldStart: boolean;
  highlightedNodeId: string | null;
  requestStart: () => void;
  confirmStart: () => void;
  cancelStart: () => void;
  setIsActive: (value: boolean) => void;
  setHighlightedNodeId: (nodeId: string | null) => void;
}

const InteractiveTutorialContext =
  createContext<InteractiveTutorialContextType | null>(null);

export function InteractiveTutorialProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isActive, setIsActive] = useState(false);
  const [shouldStart, setShouldStart] = useState(false);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(
    null,
  );

  const requestStart = useCallback(() => {
    setShouldStart(true);
  }, []);

  const confirmStart = useCallback(() => {
    setShouldStart(false);
    setIsActive(true);
  }, []);

  const cancelStart = useCallback(() => {
    setShouldStart(false);
  }, []);

  return (
    <InteractiveTutorialContext.Provider
      value={{
        isActive,
        shouldStart,
        highlightedNodeId,
        requestStart,
        confirmStart,
        cancelStart,
        setIsActive,
        setHighlightedNodeId,
      }}
    >
      {children}
    </InteractiveTutorialContext.Provider>
  );
}

export function useInteractiveTutorialContext() {
  const context = useContext(InteractiveTutorialContext);
  if (!context) {
    throw new Error(
      'useInteractiveTutorialContext must be used within InteractiveTutorialProvider',
    );
  }
  return context;
}
